import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FileText, Plus, Search, Filter, Euro, Calendar, Clock,
  CheckCircle, AlertCircle, X, Download, Send, Eye, Building2
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../../../config/api';

const statusLabels = {
  draft: { label: 'Brouillon', color: 'bg-gray-100 text-gray-700', icon: FileText },
  sent: { label: 'Envoyée', color: 'bg-blue-100 text-blue-700', icon: Send },
  paid: { label: 'Payée', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  overdue: { label: 'En retard', color: 'bg-red-100 text-red-700', icon: AlertCircle },
  cancelled: { label: 'Annulée', color: 'bg-gray-100 text-gray-500', icon: X }
};

const paymentMethods = [
  { value: 'bank_transfer', label: 'Virement bancaire' },
  { value: 'card', label: 'Carte bancaire' },
  { value: 'check', label: 'Chèque' },
  { value: 'cash', label: 'Espèces' },
  { value: 'other', label: 'Autre' }
];

export default function AdminInvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [formData, setFormData] = useState({
    client_id: '',
    quote_id: '',
    items: [],
    tax_rate: 20,
    due_days: 30,
    notes: ''
  });

  useEffect(() => {
    fetchInvoices();
    fetchStats();
    fetchClients();
    fetchQuotes();
  }, [statusFilter]);

  const fetchInvoices = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      let url = `${API_URL}/api/admin-crm/invoices`;
      if (statusFilter) url += `?status=${statusFilter}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setInvoices(data);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Erreur lors du chargement des factures');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin-crm/invoices/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchClients = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin-crm/clients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setClients(data);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchQuotes = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin-crm/quotes?status=accepted`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setQuotes(data);
      }
    } catch (error) {
      console.error('Error fetching quotes:', error);
    }
  };

  const handleCreateFromQuote = async (quoteId) => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin-crm/invoices/from-quote/${quoteId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Facture créée depuis le devis');
        fetchInvoices();
        fetchStats();
        setShowModal(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la création');
      }
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast.error('Erreur lors de la création');
    }
  };

  const handleStatusChange = async (invoice, newStatus, paymentMethod = null) => {
    const token = localStorage.getItem('admin_token');
    try {
      let url = `${API_URL}/api/admin-crm/invoices/${invoice.id}/status?status=${newStatus}`;
      if (paymentMethod) url += `&payment_method=${paymentMethod}`;
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Statut mis à jour');
        fetchInvoices();
        fetchStats();
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const filteredInvoices = invoices.filter(inv => 
    inv.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inv.client_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Available quotes for invoice creation (not yet invoiced)
  const availableQuotes = quotes.filter(q => 
    q.status === 'accepted' && 
    !invoices.some(inv => inv.quote_id === q.id)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-invoices-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Factures Admin</h1>
          <p className="text-gray-500">Gérez tes factures clients</p>
        </div>
        <Button 
          onClick={() => setShowModal(true)}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="create-invoice-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle facture
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
              <FileText className="w-4 h-4 text-gray-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats?.total || 0}</p>
          <p className="text-xs text-gray-500">Total factures</p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-4 h-4 text-green-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-green-600">{stats?.paid || 0}</p>
          <p className="text-xs text-gray-500">Payées</p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <Clock className="w-4 h-4 text-blue-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-blue-600">{stats?.pending || 0}</p>
          <p className="text-xs text-gray-500">En attente</p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
              <AlertCircle className="w-4 h-4 text-red-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-red-600">{stats?.overdue || 0}</p>
          <p className="text-xs text-gray-500">En retard</p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
              <Euro className="w-4 h-4 text-emerald-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-emerald-600">{formatCurrency(stats?.total_paid)}</p>
          <p className="text-xs text-gray-500">Encaissé</p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
              <Euro className="w-4 h-4 text-amber-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-amber-600">{formatCurrency(stats?.total_pending)}</p>
          <p className="text-xs text-gray-500">À encaisser</p>
        </motion.div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Rechercher une facture..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="invoice-search"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="invoice-status-filter"
          >
            <option value="">Tous les statuts</option>
            {Object.entries(statusLabels).map(([key, { label }]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Invoices List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {filteredInvoices.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Aucune facture trouvée</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left p-4 font-medium text-gray-600">N° Facture</th>
                  <th className="text-left p-4 font-medium text-gray-600">Client</th>
                  <th className="text-left p-4 font-medium text-gray-600">Date</th>
                  <th className="text-left p-4 font-medium text-gray-600">Échéance</th>
                  <th className="text-right p-4 font-medium text-gray-600">Montant</th>
                  <th className="text-left p-4 font-medium text-gray-600">Statut</th>
                  <th className="text-right p-4 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredInvoices.map((invoice) => {
                  const StatusIcon = statusLabels[invoice.status]?.icon || FileText;
                  return (
                    <motion.tr 
                      key={invoice.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="hover:bg-gray-50 transition-colors"
                      data-testid={`invoice-row-${invoice.id}`}
                    >
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <FileText className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{invoice.invoice_number}</p>
                            {invoice.quote_number && (
                              <p className="text-xs text-gray-400">Devis: {invoice.quote_number}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <div>
                          <p className="font-medium text-gray-900">{invoice.client_name}</p>
                          <p className="text-sm text-gray-500">{invoice.client_company || invoice.client_email}</p>
                        </div>
                      </td>
                      <td className="p-4 text-gray-600">{formatDate(invoice.issue_date)}</td>
                      <td className="p-4">
                        <span className={`${
                          invoice.status === 'overdue' ? 'text-red-600 font-medium' : 'text-gray-600'
                        }`}>
                          {formatDate(invoice.due_date)}
                        </span>
                      </td>
                      <td className="p-4 text-right">
                        <p className="font-semibold text-gray-900">{formatCurrency(invoice.total)}</p>
                        <p className="text-xs text-gray-400">HT: {formatCurrency(invoice.subtotal)}</p>
                      </td>
                      <td className="p-4">
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${statusLabels[invoice.status]?.color || 'bg-gray-100'}`}>
                          <StatusIcon className="w-3 h-3" />
                          {statusLabels[invoice.status]?.label || invoice.status}
                        </span>
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setSelectedInvoice(invoice); setShowDetailModal(true); }}
                            data-testid={`view-invoice-${invoice.id}`}
                          >
                            <Eye className="w-3 h-3" />
                          </Button>
                          {invoice.status === 'draft' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleStatusChange(invoice, 'sent')}
                              className="text-blue-600"
                            >
                              <Send className="w-3 h-3 mr-1" />
                              Envoyer
                            </Button>
                          )}
                          {(invoice.status === 'sent' || invoice.status === 'overdue') && (
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => handleStatusChange(invoice, 'paid', 'bank_transfer')}
                            >
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Payée
                            </Button>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Invoice Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl w-full max-w-lg"
            data-testid="create-invoice-modal"
          >
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Créer une facture</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {availableQuotes.length > 0 ? (
                <div>
                  <h3 className="font-medium text-gray-900 mb-3">Depuis un devis accepté</h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {availableQuotes.map(quote => (
                      <div 
                        key={quote.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div>
                          <p className="font-medium text-gray-900">{quote.quote_number}</p>
                          <p className="text-sm text-gray-500">{quote.contact_name}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-gray-900">{formatCurrency(quote.total)}</p>
                          <Button
                            size="sm"
                            onClick={() => handleCreateFromQuote(quote.id)}
                            className="mt-1 bg-blue-600 hover:bg-blue-700"
                            data-testid={`create-from-quote-${quote.id}`}
                          >
                            Créer facture
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Aucun devis accepté disponible</p>
                  <p className="text-sm mt-2">Les factures sont créées à partir des devis acceptés</p>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-100">
              <Button variant="outline" onClick={() => setShowModal(false)} className="w-full">
                Fermer
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Invoice Detail Modal */}
      {showDetailModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            data-testid="invoice-detail-modal"
          >
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{selectedInvoice.invoice_number}</h2>
                <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full mt-1 ${statusLabels[selectedInvoice.status]?.color}`}>
                  {statusLabels[selectedInvoice.status]?.label}
                </span>
              </div>
              <button onClick={() => setShowDetailModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Client Info */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{selectedInvoice.client_name}</h3>
                  <p className="text-gray-500">{selectedInvoice.client_company}</p>
                  <p className="text-gray-500">{selectedInvoice.client_email}</p>
                  {selectedInvoice.client_address && (
                    <p className="text-gray-400 text-sm mt-1">{selectedInvoice.client_address}</p>
                  )}
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-3 gap-4 bg-gray-50 rounded-lg p-4">
                <div>
                  <p className="text-xs text-gray-500">Date d'émission</p>
                  <p className="font-medium">{formatDate(selectedInvoice.issue_date)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Échéance</p>
                  <p className={`font-medium ${selectedInvoice.status === 'overdue' ? 'text-red-600' : ''}`}>
                    {formatDate(selectedInvoice.due_date)}
                  </p>
                </div>
                {selectedInvoice.paid_date && (
                  <div>
                    <p className="text-xs text-gray-500">Date de paiement</p>
                    <p className="font-medium text-green-600">{formatDate(selectedInvoice.paid_date)}</p>
                  </div>
                )}
              </div>

              {/* Items */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Lignes de facture</h4>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium text-gray-600">Description</th>
                        <th className="text-right p-3 text-sm font-medium text-gray-600">Prix</th>
                        <th className="text-right p-3 text-sm font-medium text-gray-600">Qté</th>
                        <th className="text-right p-3 text-sm font-medium text-gray-600">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {selectedInvoice.items?.map((item, index) => (
                        <tr key={index}>
                          <td className="p-3">{item.offer_name}</td>
                          <td className="p-3 text-right">{formatCurrency(item.price)}</td>
                          <td className="p-3 text-right">{item.quantity}</td>
                          <td className="p-3 text-right font-medium">{formatCurrency(item.price * item.quantity)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Totals */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-gray-600">
                  <span>Sous-total HT</span>
                  <span>{formatCurrency(selectedInvoice.subtotal)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>TVA ({selectedInvoice.tax_rate}%)</span>
                  <span>{formatCurrency(selectedInvoice.tax_amount)}</span>
                </div>
                <div className="flex justify-between font-bold text-lg pt-2 border-t border-gray-200">
                  <span>Total TTC</span>
                  <span className="text-blue-600">{formatCurrency(selectedInvoice.total)}</span>
                </div>
              </div>

              {/* Notes */}
              {selectedInvoice.notes && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Notes</h4>
                  <p className="text-gray-600 bg-gray-50 rounded-lg p-3">{selectedInvoice.notes}</p>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                Fermer
              </Button>
              {selectedInvoice.status === 'draft' && (
                <Button 
                  onClick={() => { handleStatusChange(selectedInvoice, 'sent'); setShowDetailModal(false); }}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Marquer comme envoyée
                </Button>
              )}
              {(selectedInvoice.status === 'sent' || selectedInvoice.status === 'overdue') && (
                <Button 
                  onClick={() => { handleStatusChange(selectedInvoice, 'paid', 'bank_transfer'); setShowDetailModal(false); }}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Marquer comme payée
                </Button>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
